/**
 * `<clock-in mount>/shift/:timesheetId/edit` — a worker correcting their OWN
 * shift's times and note (PUT /timesheets/:id, api #595; the clock-in since
 * api#1148).
 *
 * This was the bottom sheet over Clock In's "This period" list. It is a page
 * now, and it does what the sheet did plus one field:
 *
 *   - CLOCK IN is new. The API takes it from the owner on the SAME tenant-local
 *     day the shift is filed on, and before the clock-out; anything else is a
 *     400 whose sentence is shown as written (`invalid_clock_in`,
 *     `clock_in_changes_day`). The day is not policed here — the server owns
 *     the rule and says it better than a second copy would.
 *   - CLOCK OUT and JOB NOTES are as the sheet had them: send only what moved,
 *     so an untouched picker never re-sends a seconds-truncated time that the
 *     trail would record as an edit.
 *
 * Offered on the worker's own CLOSED rows in draft / rejected / submitted —
 * ClockInScreen links nothing else here — and re-checked on arrival, because a
 * link can be opened later or by somebody else.
 */
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { formatCalendarDate } from '../../lib/calendar-date';
import { fromDatetimeLocal, toDatetimeLocal } from '../../lib/datetime-local';
import { classifyLoadFailure } from '../../lib/load-failure';
import { isWorkerEditableStatus, type TimesheetSelfEditBody } from '../../types/timesheet-self-edit';
import { C, MI } from './clockInTheme';
import {
  ClockInPageShell, FIELD_INPUT, FIELD_LABEL, FormError, PrimaryButton, SecondaryButton,
  fieldInputStyle, useClockInReturn,
} from './ClockInPageShell';

/**
 * What the page says when PUT /timesheets/:id refuses. The 404 is the
 * service's predicate failing — the row is no longer the worker's to edit
 * (approved or locked since the list was fetched) — and the route's wording
 * is written for an API consumer. The 400s already say the one thing the hand
 * needs to fix and are shown verbatim. Anything else falls through to the
 * server message, then to a generic line; nothing is swallowed.
 */
export function selfEditRefusalMessage(err: any): string {
  const status = err?.response?.status;
  const serverMsg: string | undefined = err?.response?.data?.error;
  if (status === 404) {
    return "This shift can't be changed any more — it may have been approved since you opened it. Ask the office if it needs correcting.";
  }
  if (status === 400 && serverMsg) return serverMsg;
  if (serverMsg) return serverMsg;
  if (err?.message) return `Could not save: ${err.message}`;
  return 'Could not save. Check your connection and try again.';
}

interface OwnShiftRow {
  id: string;
  employee_id?: string;
  entry_date: string;
  status: string;
  clock_in_at: string | null;
  clock_out_at: string | null;
  worker_note?: string | null;
}

export default function OwnShiftEditScreen() {
  const { timesheetId = '' } = useParams<{ timesheetId: string }>();
  const { user } = useAuth();
  const timeZone = useTenantTimeZone();
  const qc = useQueryClient();
  const { back } = useClockInReturn();

  // Same key as ClockInScreen's linked-shift read, so its invalidation covers both.
  const shiftQuery = useQuery<OwnShiftRow | null>({
    queryKey: ['timesheets', 'one', timesheetId],
    queryFn: () => api.timesheets.get(timesheetId).then((r) => {
      const body: any = r.data;
      const row = body && typeof body === 'object' && 'data' in body ? body.data : body;
      return row && typeof row === 'object' ? (row as OwnShiftRow) : null;
    }),
    enabled: !!timesheetId,
    retry: (n, err: any) => {
      const st = err?.response?.status;
      return st !== 404 && st !== 400 && st !== 401 && st !== 403 && n < 2;
    },
  });
  const shift = shiftQuery.data ?? null;
  const mine = !!shift && !!user?.id && shift.employee_id === user.id;
  const editable = mine && isWorkerEditableStatus(shift!.status) && !!shift!.clock_out_at;

  /*
    The picker strings the page OPENED with are kept beside the live ones, so
    "unchanged" and "changed to the same minute" stay different things.
  */
  const [clockIn, setClockIn] = useState('');
  const [clockOut, setClockOut] = useState('');
  const [note, setNote] = useState('');
  const [initial, setInitial] = useState<{ clockIn: string; clockOut: string; note: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!shift || initial) return;
    const next = {
      clockIn: toDatetimeLocal(shift.clock_in_at, timeZone),
      clockOut: toDatetimeLocal(shift.clock_out_at, timeZone),
      note: shift.worker_note ?? '',
    };
    setInitial(next);
    setClockIn(next.clockIn);
    setClockOut(next.clockOut);
    setNote(next.note);
  }, [shift, initial, timeZone]);

  const save = useMutation({
    mutationFn: async () => {
      if (!shift || !initial) throw new Error('No shift loaded');
      const body: TimesheetSelfEditBody = {};
      if (clockIn !== initial.clockIn) {
        const iso = fromDatetimeLocal(clockIn, timeZone);
        if (!iso) throw Object.assign(new Error('Pick a clock-in time.'), { local: true });
        body.clock_in_at = iso;
      }
      if (clockOut !== initial.clockOut) {
        const iso = fromDatetimeLocal(clockOut, timeZone);
        if (!iso) throw Object.assign(new Error('Pick a clock-out time.'), { local: true });
        body.clock_out_at = iso;
      }
      if (note !== initial.note) body.worker_note = note;
      if (Object.keys(body).length === 0) return null;
      const res = await api.timesheets.update(shift.id, body);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['timesheets', 'my-period'] });
      qc.invalidateQueries({ queryKey: ['timesheets', 'one'] });
      qc.invalidateQueries({ queryKey: ['timesheet-status'] });
      // As the sheet did: "Shift updated" even when nothing moved (nothing was sent).
      back('Shift updated');
    },
    // Stays on the page, under the button that failed.
    onError: (err: any) => setError(err?.local ? err.message : selfEditRefusalMessage(err)),
  });

  const status = (shiftQuery.error as any)?.response?.status;
  const problem: string | null = shiftQuery.isLoading
    ? null
    : status === 404 || status === 400 || (shiftQuery.isSuccess && !mine)
      ? "That shift isn't one of yours, or it no longer exists."
      : shiftQuery.error
        ? classifyLoadFailure(shiftQuery.error) === 'refused'
          ? "You don't have permission to view that shift."
          : "Couldn't load that shift. Check your connection and try again."
        : shift && !editable
          ? shift.clock_out_at
            ? "This shift can't be changed any more — it may have been approved. Ask the office if it needs correcting."
            : "This shift is still running. Clock out first; then its times can be corrected."
          : null;

  const dayLabel = shift ? formatCalendarDate(shift.entry_date, { weekday: 'short', month: 'short', day: 'numeric' }) ?? '—' : undefined;

  return (
    <ClockInPageShell
      title="Edit shift"
      subtitle={dayLabel && (
        <>
          {dayLabel}
          {shift?.status === 'submitted' && ' · already submitted — the office will see this was changed'}
        </>
      )}
      onBack={() => back()}
    >
      {shiftQuery.isLoading && (
        <div className="flex justify-center py-12">
          <MI name="hourglass_top" size={32} className="animate-spin" style={{ color: C.primary }} />
        </div>
      )}
      {problem && (
        <p role="status" className="rounded-xl px-4 py-3 text-sm" style={{ background: C.surface, color: C.onSurface, border: '1px solid var(--ci-edge-25)' }}>
          {problem}
        </p>
      )}
      {editable && initial && (
        <form
          aria-label="Edit shift"
          className="rounded-2xl p-5"
          style={{ background: C.surface }}
          onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
        >
          <label htmlFor="edit-clock-in" className={FIELD_LABEL} style={{ color: C.onSurfaceVariant }}>Clock in</label>
          <input
            id="edit-clock-in"
            type="datetime-local"
            value={clockIn}
            onChange={(e) => { setClockIn(e.target.value); setError(null); }}
            aria-describedby="edit-clock-in-hint"
            className={FIELD_INPUT}
            style={fieldInputStyle}
          />
          <p id="edit-clock-in-hint" className="text-xs mt-1.5" style={{ color: C.onSurfaceVariant }}>
            Same day only — the day decides which pay period the shift is in.
          </p>

          <label htmlFor="edit-clock-out" className={`${FIELD_LABEL} mt-4`} style={{ color: C.onSurfaceVariant }}>Clock out</label>
          <input
            id="edit-clock-out"
            type="datetime-local"
            value={clockOut}
            onChange={(e) => { setClockOut(e.target.value); setError(null); }}
            className={FIELD_INPUT}
            style={fieldInputStyle}
          />

          <label htmlFor="edit-note" className={`${FIELD_LABEL} mt-4`} style={{ color: C.onSurfaceVariant }}>Job notes</label>
          <textarea
            id="edit-note"
            value={note}
            onChange={(e) => { setNote(e.target.value); setError(null); }}
            placeholder="What did you work on?"
            className={`${FIELD_INPUT} resize-none`}
            style={{ ...fieldInputStyle, minHeight: 80 }}
          />

          {error && <FormError>{error}</FormError>}

          <div className="space-y-2 mt-5">
            <PrimaryButton busy={save.isPending} onClick={() => save.mutate()}>Save changes</PrimaryButton>
            <SecondaryButton disabled={save.isPending} onClick={() => back()}>Cancel</SecondaryButton>
          </div>
        </form>
      )}
    </ClockInPageShell>
  );
}
